import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "candidateSelect",
    "competenciesContainer",
    "competenciesSection",
    "courseId",
    "ratingTool",
    "ratingToolLabel",
    "encounterKindSelect",
    "patientNumberField",
    "caseSelect",
    "demographicsInput",
    "callSummaryInput",
    "callSummaryLabel",
    "overallCommentsInput",
    "learnerReflectionField",
    "grsSection",
    "miniCexSection",
  ];

  static values = {
    draftCompetencies: { type: Object, default: {} },
  };

  connect() {
    if (this.hasCandidateSelectTarget) {
      if (this.candidateSelectTarget.value) {
        this.loadCandidateData(this.candidateSelectTarget.value);
      }
    }
    this.applyEncounterKindUI();
    this.hydrateScoreDefinitions();

    const form = this.element.querySelector("form");
    if (form) {
      form.addEventListener("submit", this.validateBeforeSubmit.bind(this));
    }
  }

  async candidateChanged(event) {
    const candidateId = event.target.value;
    if (!candidateId) {
      this.clearFormState();
      return;
    }

    await this.loadCandidateData(candidateId);
  }

  encounterKindChanged() {
    this.applyEncounterKindUI();
  }

  async loadCandidateData(candidateId) {
    this.showLoading();

    try {
      const response = await fetch(
        `/direct_field_ratings/competencies_for_candidate?candidate_id=${candidateId}`,
        {
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
        }
      );
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        const body = await response.text();
        throw new Error(
          this.extractErrorMessage(body) || "Unexpected response while loading competencies."
        );
      }
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || "Could not load candidate data.");
      }

      if (this.hasCompetenciesContainerTarget) {
        this.competenciesContainerTarget.innerHTML = payload.html;
      }
      if (this.hasCourseIdTarget) {
        this.courseIdTarget.value = payload.course_id || "";
      }
      if (this.hasRatingToolTarget) {
        this.ratingToolTarget.value = payload.rating_tool || "";
      }
      this.toggleRatingToolSections(payload.rating_tool);
      this.applyEncounterKindUI();
      requestAnimationFrame(() => this.applyDraftCompetencies());
    } catch (error) {
      this.showError(error.message);
      if (this.hasRatingToolLabelTarget) {
        this.ratingToolLabelTarget.textContent = error.message;
      }
      this.toggleRatingToolSections(null);
      this.applyEncounterKindUI();
    }
  }

  showLoading() {
    if (!this.hasCompetenciesContainerTarget) {
      return;
    }
    this.competenciesContainerTarget.innerHTML = `
      <div class="text-center py-3">
        <div class="spinner-border text-primary" role="status"></div>
      </div>
    `;
  }

  showError(message) {
    if (!this.hasCompetenciesContainerTarget) {
      return;
    }
    this.competenciesContainerTarget.innerHTML = `
      <div class="alert alert-danger mb-0">${this.escapeHtml(message)}</div>
    `;
  }

  clearFormState() {
    if (this.hasCompetenciesContainerTarget) {
      this.competenciesContainerTarget.innerHTML = `
        <div class="alert alert-info mb-0">Select a candidate to load competencies.</div>
      `;
    }
    if (this.hasCourseIdTarget) {
      this.courseIdTarget.value = "";
    }
    if (this.hasRatingToolTarget) {
      this.ratingToolTarget.value = "";
    }
    this.toggleRatingToolSections(null);
    this.applyEncounterKindUI();
  }

  toggleRatingToolSections(toolName) {
    const normalized = (toolName || "").toString().toLowerCase();
    const isGrs = ["grs", "global rating scale", "global_rating_scale"].includes(normalized);
    const isMiniCex = ["mini-cex", "mini_cex", "minicex"].includes(normalized);

    const isGeneralFeedback = this.selectedEncounterKind() === "general_feedback";

    if (this.hasGrsSectionTarget) {
      this.grsSectionTarget.style.display = isGrs && !isGeneralFeedback ? "block" : "none";
    }
    if (this.hasMiniCexSectionTarget) {
      this.miniCexSectionTarget.style.display = isMiniCex && !isGeneralFeedback ? "block" : "none";
    }
    if (this.hasRatingToolLabelTarget) {
      if (isGrs) {
        this.ratingToolLabelTarget.textContent = "GRS";
      } else if (isMiniCex) {
        this.ratingToolLabelTarget.textContent = "Mini-CEX";
      } else {
        this.ratingToolLabelTarget.textContent =
          "Select a candidate to load rating tool.";
      }
    }
  }

  selectedEncounterKind() {
    if (!this.hasEncounterKindSelectTarget) {
      return "patient_encounter";
    }
    const raw = (this.encounterKindSelectTarget.value || "").toString().trim().toLowerCase();
    if (["patient_encounter", "simulation", "general_feedback"].includes(raw)) {
      return raw;
    }
    return "patient_encounter";
  }

  applyEncounterKindUI() {
    const kind = this.selectedEncounterKind();
    const isSimulation = kind === "simulation";
    const isGeneralFeedback = kind === "general_feedback";
    const requiresCase = !isGeneralFeedback;

    if (this.hasPatientNumberFieldTarget) {
      this.patientNumberFieldTarget.style.display = isSimulation || isGeneralFeedback ? "none" : "";
    }

    if (this.hasCaseSelectTarget) {
      this.caseSelectTarget.required = requiresCase;
      const caseInput = this.caseSelectTarget.closest(".input");
      if (caseInput) {
        caseInput.classList.toggle("required", requiresCase);
      }
    }

    if (this.hasDemographicsInputTarget) {
      this.demographicsInputTarget.required = !isGeneralFeedback;
      const container = this.demographicsInputTarget.closest(".input");
      if (container) {
        container.style.display = isGeneralFeedback ? "none" : "";
      }
    }

    if (this.hasCallSummaryInputTarget) {
      this.callSummaryInputTarget.required = !isGeneralFeedback;
      const container = this.callSummaryInputTarget.closest("div");
      if (container) {
        container.style.display = isGeneralFeedback ? "none" : "";
      }
    }

    if (this.hasCallSummaryLabelTarget) {
      this.callSummaryLabelTarget.textContent = isSimulation ? "Scenario Summary *" : "Call Summary *";
    }

    if (this.hasLearnerReflectionFieldTarget) {
      this.learnerReflectionFieldTarget.style.display = isGeneralFeedback ? "none" : "";
    }

    if (this.hasCompetenciesSectionTarget) {
      this.competenciesSectionTarget.style.display = isSimulation || isGeneralFeedback ? "none" : "";
    }

    if (this.hasOverallCommentsInputTarget) {
      this.overallCommentsInputTarget.required = isGeneralFeedback;
    }

    this.toggleRatingToolSections(this.hasRatingToolTarget ? this.ratingToolTarget.value : "");
  }

  scoreChanged(event) {
    const descriptionId = event.target.dataset.descriptionId;
    const definition = event.target.dataset.definition || "";
    if (!descriptionId) {
      return;
    }
    const descriptionEl = this.element.querySelector(`#${descriptionId}`);
    if (!descriptionEl) {
      return;
    }
    descriptionEl.textContent = definition;
    descriptionEl.classList.remove("d-none");
  }

  hydrateScoreDefinitions() {
    const checkedRadios = this.element.querySelectorAll(
      "input[type='radio'][data-description-id]:checked"
    );
    checkedRadios.forEach((radio) => {
      const descriptionId = radio.dataset.descriptionId;
      const definition = radio.dataset.definition || "";
      if (!descriptionId) {
        return;
      }
      const descriptionEl = this.element.querySelector(`#${descriptionId}`);
      if (!descriptionEl) {
        return;
      }
      descriptionEl.textContent = definition;
      descriptionEl.classList.remove("d-none");
    });
  }

  validateBeforeSubmit(event) {
    this.restoreSubmitButtons(event.target);

    const errors = [];
    const tool = this.hasRatingToolTarget ? this.ratingToolTarget.value : "";
    const normalized = (tool || "").toLowerCase();
    const isGrs = ["grs", "global rating scale", "global_rating_scale"].includes(normalized);
    const isMiniCex = ["mini-cex", "mini_cex", "minicex"].includes(normalized);
    const encounterKind = this.selectedEncounterKind();
    const isSimulation = encounterKind === "simulation";
    const isGeneralFeedback = encounterKind === "general_feedback";

    const grsDomainLabels = {
      sa: "Situation Awareness", hg: "History Gathering", pa: "Patient Assessment",
      dm: "Decision Making", ru: "Resource Utilization", co: "Communication", ps: "Procedural Skills"
    };
    const miniCexDomainLabels = {
      ca: "Clinical Abilities", prc: "Patient Related Communication",
      phq: "Professionalism", trq: "Teamwork", nts: "Non-Technical Skills"
    };

    if (isGeneralFeedback) {
      if (this.hasOverallCommentsInputTarget && !this.overallCommentsInputTarget.value.trim()) {
        errors.push("Overall comments are required for General Feedback.");
      }
    } else if (isGrs) {
      const domains = Object.keys(grsDomainLabels);
      const missing = domains.filter((d) => {
        const radios = this.element.querySelectorAll(`input[type="radio"][name="direct_field_rating[${d}_value]"]`);
        return !Array.from(radios).some((r) => r.checked);
      });
      if (missing.length > 0) {
        const names = missing.map((d) => grsDomainLabels[d]).join(", ");
        errors.push(`GRS scores required for: ${names}`);
      }
    } else if (isMiniCex) {
      const domains = Object.keys(miniCexDomainLabels);
      const missing = domains.filter((d) => {
        const radios = this.element.querySelectorAll(`input[type="radio"][name="direct_field_rating[mini_cex_scores][${d}]"]`);
        return !Array.from(radios).some((r) => r.checked);
      });
      if (missing.length > 0) {
        const names = missing.map((d) => miniCexDomainLabels[d]).join(", ");
        errors.push(`Mini-CEX scores required for: ${names}`);
      }
    }

    if (!isSimulation && !isGeneralFeedback) {
      const checkedCompetencies = this.element.querySelectorAll(
        'input[type="checkbox"][name*="[selected]"]:checked'
      );
      if (checkedCompetencies.length > 0) {
        let unrated = 0;
        checkedCompetencies.forEach((cb) => {
          const container = cb.closest("[data-controller='competency-selection']");
          if (container && !container.querySelector('input[type="radio"][name*="[rating_value]"]:checked')) {
            unrated++;
          }
        });
        if (unrated > 0) {
          errors.push(`${unrated} selected competenc${unrated === 1 ? "y needs a" : "ies need"} performance rating.`);
        }
      }
    }

    if (errors.length > 0) {
      event.preventDefault();
      // Prevent Rails UJS from scheduling a delayed disable for aborted submits.
      event.stopPropagation();
      let banner = this.element.querySelector(".dfr__validation-errors");
      if (!banner) {
        banner = document.createElement("div");
        banner.className = "dfr__validation-errors dfr__validation-errors--below-submit";
        const form = this.element.querySelector("form");
        const submitBar = form ? form.querySelector(".dfr__bar") : null;
        if (submitBar) {
          submitBar.insertAdjacentElement("afterend", banner);
        } else if (form) {
          form.prepend(banner);
        }
      }
      banner.innerHTML = `<strong>Please fix the following:</strong><ul>${errors.map((e) => `<li>${this.escapeHtml(e)}</li>`).join("")}</ul>`;
      // Run again on the next tick in case another handler toggled disabled.
      window.setTimeout(() => this.restoreSubmitButtons(event.target), 0);
      banner.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } else {
      const banner = this.element.querySelector(".dfr__validation-errors");
      if (banner) banner.remove();
    }
  }

  saveDraft(event) {
    const form = this.element.querySelector("form");
    if (!form) return;

    let draftInput = form.querySelector('input[name="save_as_draft"]');
    if (!draftInput) {
      draftInput = document.createElement("input");
      draftInput.type = "hidden";
      draftInput.name = "save_as_draft";
      form.appendChild(draftInput);
    }
    draftInput.value = "1";

    form.noValidate = true;

    const banner = this.element.querySelector(".dfr__validation-errors");
    if (banner) banner.remove();

    form.submit();
  }

  applyDraftCompetencies() {
    const competencies = this.draftCompetenciesValue;
    if (!competencies || Object.keys(competencies).length === 0) return;

    Object.values(competencies).forEach((comp) => {
      if (!comp.selected || comp.selected !== "1") return;
      const loId = comp.learning_objective_id;
      if (!loId) return;

      const checkbox = this.element.querySelector(
        `input[type="checkbox"][name*="[selected]"][data-learning-objective-id="${loId}"]`
      );
      if (!checkbox) return;

      checkbox.checked = true;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));

      if (comp.rating_value) {
        const container = checkbox.closest("[data-controller='competency-selection']");
        if (container) {
          const radio = container.querySelector(
            `input[type="radio"][value="${comp.rating_value}"]`
          );
          if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
      }

      if (comp.comments) {
        const container = checkbox.closest("[data-controller='competency-selection']");
        if (container) {
          const textarea = container.querySelector("textarea");
          if (textarea) textarea.value = comp.comments;
        }
      }
    });
  }

  restoreSubmitButtons(form) {
    if (!form) return;

    const submitButtons = form.querySelectorAll(
      'button[type="submit"], input[type="submit"]'
    );

    submitButtons.forEach((button) => {
      button.disabled = false;
      button.classList.remove("disabled");

      const enableWith = button.getAttribute("data-ujs-enable-with");
      if (enableWith !== null) {
        if (button.tagName === "INPUT") {
          button.value = enableWith;
        } else {
          button.innerHTML = enableWith;
        }
        button.removeAttribute("data-ujs-enable-with");
      }
    });
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  extractErrorMessage(html) {
    if (!html) {
      return null;
    }
    if (html.includes("You need to sign in or sign up before continuing")) {
      return "Your session appears to have expired. Please sign in again.";
    }
    return null;
  }
}
